import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getContractApiUrl, isProductionEnvironment } from '../utils/apiUrls';

// Backend URL configuration
const BACKEND_URL = getContractApiUrl();

// All EmailAttachmentSplit columns in order
const COLUMN_ORDER = [
  'id',
  'email_attachment_id',
  'contract_id',
  'listing_id',
  'buyer_agreement_id',
  'referral_agreement_id',
  'property_address',
  'buyer_name',       // Joined from Contract
  'seller_name',      // Joined from Contract
  'email_filename',
  'split_attachment_file_name',
  'doc_file_type',
  'gcs_uri',
  'preview_image_gcs_uri',
  'email_timestamp',
  'doc_title',
  'core_file_identifier',
  'version',
  'created_at',
  'updated_at',
];

function DeveloperSplitAttachments() {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation(); // Use useLocation to get current path

  const [pageSize, setPageSize] = useState(20); // 10 | 20 | 50
  const [splitAttachments, setSplitAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isProd = isProductionEnvironment();

  // Filters
  const [contractId, setContractId] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [sellerName, setSellerName] = useState('');
  const [dateStart, setDateStart] = useState(''); // ISO date (yyyy-mm-dd)
  const [dateEnd, setDateEnd] = useState('');

  const isKeyFloUser = useMemo(() => {
    const email = user?.email || '';
    const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
    return ALLOWED_USERS.includes(email);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isKeyFloUser) {
      // Redirect non-KeyFlo users back to home
      navigate('/');
    }
  }, [isAuthenticated, isKeyFloUser, navigate]);

  const formatDateTime = (ts) => {
    try {
      return new Date(ts).toLocaleString();
    } catch (e) {
      return ts || '';
    }
  };

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      // If already in YYYY-MM-DD format, return as-is (no timezone conversion)
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      // If it's a date-only string in YYYY-MM-DD format, parse components directly to avoid timezone issues
      const dateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
      }
      // Otherwise, parse and format as YYYY-MM-DD (for other date formats)
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr || '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return dateStr || '';
    }
  };

  const buildParams = (resetOffset = true) => {
    const params = {
      limit: pageSize,
      offset: resetOffset ? 0 : offset
    };

    if (contractId) params.contract_id = contractId;
    if (buyerName) params.buyer_name = buyerName;
    if (sellerName) params.seller_name = sellerName;
    if (dateStart) params.start_date = dateStart;
    if (dateEnd) params.end_date = dateEnd;
    return params;
  };

  const fetchSplitAttachments = async (reset = true) => {
    if (!isAuthenticated || !isKeyFloUser) return;
    try {
      setLoading(true);
      setError('');

      const params = buildParams(reset);
      const response = await axios.get(`${BACKEND_URL}/split-attachments`, { params });

      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message || 'Failed to fetch split attachments');
      }

      const newAttachments = response.data.split_attachments || [];
      setSplitAttachments(reset ? newAttachments : [...splitAttachments, ...newAttachments]);
      setTotalCount(response.data.total || 0);
      setOffset((reset ? 0 : offset) + newAttachments.length);
    } catch (e) {
      setError(e.message || 'Failed to load split attachments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isKeyFloUser) fetchSplitAttachments(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isKeyFloUser, pageSize]);

  const handleDeleteClick = (attachment) => {
    setAttachmentToDelete(attachment);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!attachmentToDelete) return;
    try {
      setDeleting(true);
      const response = await axios.delete(`${BACKEND_URL}/split-attachments/${attachmentToDelete.id}/hard`, {
        params: { caller_email: user?.email },
      });
      if (response.data?.error) throw new Error(response.data.error);
      setSplitAttachments(prev => prev.filter(a => a.id !== attachmentToDelete.id));
      setTotalCount(prev => prev - 1);
    } catch (e) {
      alert(e.response?.data?.error || e.message || 'Failed to delete split attachment');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setAttachmentToDelete(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
        <p>Please log in</p>
        <button onClick={() => loginWithRedirect({
          authorizationParams: {
            prompt: 'login'
          }
        })}>Log in</button>
      </div>
    );
  }

  if (!isKeyFloUser) {
    return (
      <div style={{ padding: '24px' }}>
        <h2>Access Denied</h2>
        <p>This page is restricted to KeyFlo team members.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>Developer • Split Attachment Files</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
          <label style={{ marginRight: '8px' }}>Page size:</label>
          <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          {/* Hamburger Menu */}
          <DeveloperNavigation />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Contract ID</label>
          <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="contract_id" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Buyer Name</label>
          <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} placeholder="buyer_name" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Seller Name</label>
          <input value={sellerName} onChange={(e) => setSellerName(e.target.value)} placeholder="seller_name" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Start Date</label>
          <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>End Date</label>
          <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} style={{ width: '100%' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button onClick={() => { setOffset(0); fetchSplitAttachments(true); }}>Apply Filters</button>
        <button onClick={() => { setContractId(''); setBuyerName(''); setSellerName(''); setDateStart(''); setDateEnd(''); setOffset(0); fetchSplitAttachments(true); }}>Clear</button>
        <button onClick={() => fetchSplitAttachments(false)}>Load More</button>
      </div>

      {loading && <LoadingSpinner />}
      {error && (
        <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>
      )}

      <div style={{ marginBottom: '12px' }}>
        Showing {splitAttachments.length} of {totalCount} split attachments
      </div>

      <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
        <table style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', position: 'sticky', top: 0, left: 0, zIndex: 2, minWidth: 90, width: 90 }}>
                actions
              </th>
              {COLUMN_ORDER.map((col) => (
                <th
                  key={col}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderBottom: '1px solid #e5e7eb',
                    background: '#f8fafc',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {splitAttachments.map((attachment, idx) => (
              <tr key={attachment.id || idx} style={{ borderBottom: '1px solid #f1f5f9', height: '100px', maxHeight: '100px' }}>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top', position: 'sticky', left: 0, zIndex: 1, background: '#ffffff', minWidth: 90, width: 90 }}>
                  <button
                    onClick={() => handleDeleteClick(attachment)}
                    style={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                    }}
                  >
                    Delete
                  </button>
                </td>
                {COLUMN_ORDER.map((col) => {
                  let val = attachment[col];
                  if (col === 'email_timestamp' || col === 'created_at' || col === 'updated_at') {
                    val = val ? formatDateTime(val) : '';
                  }
                  if (val === null || typeof val === 'undefined') val = '';
                  return (
                    <td
                      key={col}
                      style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid #f1f5f9',
                        borderRight: '1px solid #f1f5f9',
                        verticalAlign: 'top',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        minWidth: 280
                      }}
                    >
                      <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                        {String(val)}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            {splitAttachments.length === 0 && !loading && (
              <tr>
                <td colSpan={COLUMN_ORDER.length + 1} style={{ padding: '12px', textAlign: 'center' }}>No split attachments found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showDeleteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', width: '420px', maxWidth: '90%' }}>
            <h3 style={{ marginTop: 0, color: '#ef4444' }}>Confirm Delete</h3>
            {isProd && (
              <p style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '1rem', textAlign: 'center', border: '2px solid #dc2626', borderRadius: '4px', padding: '8px' }}>
                THIS IS THE PROD ENV. PLEASE BE 100% SURE YOU WANT TO DELETE.
              </p>
            )}
            <p>
              Are you sure you want to permanently delete split attachment <strong>#{attachmentToDelete?.id}</strong>
              {attachmentToDelete?.split_attachment_file_name ? ` — ${attachmentToDelete.split_attachment_file_name}` : ''}?
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
              <button
                onClick={() => { setShowDeleteModal(false); setAttachmentToDelete(null); }}
                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                style={{ background: '#ef4444', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
              >
                {deleting ? 'Deleting...' : 'Delete Split'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeveloperSplitAttachments;

